import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, forkJoin, of, map, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

import {
  AlertType,
  CategorizedTeacherNotifications,
  DateGroupedTeacherNotifications,
  TeacherUsageNotification,
  TeacherNotificationResponse,
  TeacherUsageNotificationResponse,
} from '../models/notification.model';
import { LoadingService } from './loading.service';

@Injectable({
  providedIn: 'root'
})
export class NotificationsService {
  private http = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private reqOpts = { withCredentials: true };

  /**
   * This method loads standard Teacher Notifications (based on Student alerts) and merges in
   * Teacher Usage Notifications (based on weekly usage for a teachers students). This shows
   * what a user would consider all 'Notifications'
   */
  getHistoryNotifications(startDate?: string, endDate?: string): Observable<TeacherNotificationResponse> {
    let params = new HttpParams();
    if (startDate) {
      params = params.set('startDate', startDate);
    }

    if (endDate) {
      params = params.set('endDate', endDate);
    }

    const allParams = { ...this.reqOpts, params };

    const standardHistory$ = this.http.get<DateGroupedTeacherNotifications>(
      `${environment.dataServicesURL}manage/notifications/teacher/history`,
      allParams
    );

    const usageHistory$ = this.http.get<TeacherUsageNotificationResponse[]>(
      `${environment.dataServicesURL}manage/notifications/teacher/usage/history`,
      allParams
    );

    return this.fetchAndMergeNotifications(standardHistory$, usageHistory$);
  }

  /**
   * This method loads standard Teacher Notifications (based on Student alerts) and merges in
   * Teacher Usage Notifications (based on weekly usage for a teachers students). This shows
   * what a user would consider all 'Notifications'
   */
  getRecentNotifications(): Observable<TeacherNotificationResponse> {
    const standardRecent$ = this.http.get<DateGroupedTeacherNotifications>(
      `${environment.dataServicesURL}manage/notifications/teacher/recent`,
      this.reqOpts
    );

    const usageRecent$ = this.http.get<TeacherUsageNotificationResponse[]>(
      `${environment.dataServicesURL}manage/notifications/teacher/usage/recent`,
      this.reqOpts
    );

    return this.fetchAndMergeNotifications(standardRecent$, usageRecent$);
  }

  dismissNotifications(alertIds: number[], notificationIds: number[]): Observable<any> {
    return this.http.patch(
      `${environment.dataServicesURL}manage/notifications/dismiss`,
      { alertIds, notificationIds },
      this.reqOpts
    );
  }

  filterNotifications(notifications: DateGroupedTeacherNotifications, term: string): DateGroupedTeacherNotifications {
    if (!term || !term.trim()) return notifications;

    const normalizedTerm = term.toLowerCase().trim();
    const filteredNotifications: DateGroupedTeacherNotifications = {};

    // Filter notifications by term, attempting to match alert type, category or student name
    // For all of our dates
    for (const [date, categories] of Object.entries(notifications)) {
      const filteredCategories: CategorizedTeacherNotifications = {};

      // Check all categories and notifications within
      for (const [category, categoryNotifications] of Object.entries(categories ?? {})) {
        const categoryMatches = category.toLowerCase().includes(normalizedTerm);

        // Check the notifications themselves
        const notificationMatches = categoryNotifications.filter(notif => {
          // If the category matches, then we want to automatically include this notification
          if (categoryMatches) return true;

          // Search the student name
          if ('studentName' in notif && notif.studentName?.toLowerCase().trim().includes(normalizedTerm)) return true;

          // Search the notification text
          return notif.text?.toLowerCase().trim().includes(normalizedTerm);
        });

        // If we have any matches, add them to the filtered categories
        if (notificationMatches.length > 0) {
          filteredCategories[category as AlertType] = notificationMatches;
        }
      }

      // If we have had matches on this category, add it to the filtered notifications by its date
      if (Object.keys(filteredCategories).length > 0) {
        filteredNotifications[date] = filteredCategories;
      }
    }

    return filteredNotifications;
  }

  private fetchAndMergeNotifications(
    standard$: Observable<DateGroupedTeacherNotifications>,
    usage$: Observable<TeacherUsageNotificationResponse[]>
  ): Observable<TeacherNotificationResponse> {
    this.loadingService.start();

    return forkJoin({ standard: standard$, usage: usage$ }).pipe(
      map(({ standard, usage }) => {
        this.loadingService.finish();
        return {
          isErred: false,
          data: this.mergeUsageNotifications(standard, usage),
        };
      }),
      catchError((error) => {
        this.loadingService.finish();
        return of({
          isErred: true,
          errorMsg: error.message ?? 'Unknown error',
        });
      })
    );
  }

  private mergeUsageNotifications(
    standard: DateGroupedTeacherNotifications,
    usage: TeacherUsageNotificationResponse[]
  ): DateGroupedTeacherNotifications {
    // Our standard notifications comeback as we need, so spread them into our result
    const merged: DateGroupedTeacherNotifications = { ...standard };

    // Our TeacherUsageNotification responses need some massaging to get our data
    for (const item of usage ?? []) {
      const parsedUsage: TeacherUsageNotification = {
        notificationId: item.notificationId,
        teacherId: item.teacherId,
        text: item.text,
        category: item.category,
        buckets: item.countsJson ? JSON.parse(item.countsJson) : [],
        dismissedAt: item.dismissedAt ? new Date(item.dismissedAt) : (null as any),
        createdAt: new Date(item.createdAt)
      };

      // Now, for this usage notification, find our date and merge it in so it shows properly
      const dateKey = parsedUsage.createdAt.toISOString().split('T')[0]
      const categoryKey = 'time-usage';

      // Any existing data at the current date group
      const dateGroup = merged[dateKey] ?? {};
      const categoryList = dateGroup[categoryKey] ?? [];

      // Put it all together
      merged[dateKey] = {
        ...dateGroup,
        [categoryKey]: [...categoryList, parsedUsage]
      };
    }

    return merged;
  }
}
